import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import EditIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Edit.js';
import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  Container,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import { reportDetailsVm } from 'src/components/pages/Case/Case.slice';
import {
  CaseType,
  useUpdateReportRelationshipToOrganisationMutation,
} from 'src/graphql';
import theme from 'src/theme/theme';
import { CaseTypeEnum } from 'src/types';

export interface EmployeeReporter {
  displayName?: string | null;
  name?: string | null;
  email?: string | null;
  eid?: string | null;
  role?: string | null;
  department?: string | null;
  site?: string | null;
  country?: string | null;
}

interface DisclosureEmployeeDetailsProps {
  caseById?: CaseType;
  reporter?: EmployeeReporter | null;
  caseType: CaseTypeEnum;
}

type OverviewRow = {
  key: string;
  type: 'text' | 'relationship';
  value: string;
};

export const DisclosureEmployeeDetails = ({
  caseById,
  reporter: reporterProp,
  caseType,
}: DisclosureEmployeeDetailsProps) => {
  const { tokens } = useTheme<LensTheme>();

  const fallbackValue = '-';
  const reportId = caseById?.Reports?.[0]?.id;

  const {
    reporter,
    relationshipTypes,
    relationshipType,
    updateRelationshipToOrgEnabled,
    permissions: { canUpdateRelationshipToOrg },
  } = useSelector(reportDetailsVm({ reportId: reportId ?? '' }));

  const queryClient = useQueryClient();
  const updateReporterRelationshipToOrganisation =
    useUpdateReportRelationshipToOrganisationMutation({
      onSuccess: () => {
        queryClient.invalidateQueries(['CaseById']);
        handleSuccessToast({
          toastMessage: 'Relationship to organisation updated successfully',
        });
      },
      onError: (error) => {
        console.error(
          'DisclosureEmployeeDetails: failed to update relationship to organisation',
          { reportId, error },
        );
        handleErrorToast({
          toastMessage: 'Failed to update relationship to organisation',
        });
      },
    });

  const [showRelationshipDropdown, setShowRelationshipDropdown] =
    useState(false);
  const reporterDetails = reporterProp ?? reporter;

  const canUpdateRelationship =
    updateRelationshipToOrgEnabled && canUpdateRelationshipToOrg;
  const RelationshipChange = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
      }}
    >
      <FormControl sx={{ width: '100%' }}>
        <Select
          value={String(relationshipType?.value ?? '')}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (!canUpdateRelationshipToOrg || Number.isNaN(value) || !reportId)
              return;
            updateReporterRelationshipToOrganisation.mutate({
              report: reportId,
              relationshipToOrganisation: value,
            });
            setShowRelationshipDropdown(false);
          }}
        >
          {relationshipTypes.map((r) => (
            <MenuItem key={r.label} value={String(r.value)}>
              {r.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <IconButton
        size="small"
        onClick={() => setShowRelationshipDropdown(false)}
        data-testid="cancel-edit-relationship-button"
        aria-label="Cancel editing relationship"
        sx={{ marginLeft: 'auto' }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );

  const renderValue = (row: OverviewRow) => {
    if (row.type === 'relationship') {
      if (showRelationshipDropdown) {
        return RelationshipChange;
      }
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <span>{relationshipType?.label ?? fallbackValue}</span>
          {caseType === CaseTypeEnum.SPEAK_UP && canUpdateRelationship && (
            <IconButton
              size="small"
              onClick={() => setShowRelationshipDropdown(true)}
              data-testid="edit-relationship-button"
              aria-label="Edit relationship to organisation"
              sx={{ marginLeft: 'auto' }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
      );
    }
    return row.value;
  };

  const rows = [
    {
      key: 'Name',
      type: 'text',
      value:
        (reporterDetails as EmployeeReporter)?.displayName ??
        reporterDetails?.name ??
        fallbackValue,
    },
    { key: 'ID', type: 'text', value: reporterDetails?.eid ?? fallbackValue },
    {
      key: 'E-mail',
      type: 'text',
      value: reporterDetails?.email ?? fallbackValue,
    },
    {
      key: 'Role',
      type: 'text',
      value: reporterDetails?.role ?? fallbackValue,
    },
    {
      key: 'Department',
      type: 'text',
      value: reporterDetails?.department ?? fallbackValue,
    },
    {
      key: 'Country',
      type: 'text',
      value: reporterDetails?.country ?? fallbackValue,
    },
    {
      key: 'Site',
      type: 'text',
      value: reporterDetails?.site ?? fallbackValue,
    },
    caseType === CaseTypeEnum.SPEAK_UP && {
      key: 'Relationship to org.',
      type: 'relationship',
      value: relationshipType?.label ?? fallbackValue,
    },
  ].filter(Boolean) as OverviewRow[];

  const label = caseType === CaseTypeEnum.SPEAK_UP ? 'Reporter' : 'Employee';

  return (
    <Container
      sx={{
        backgroundColor: theme.atlas.semantic.color.surface.default,
        borderRadius: tokens.semantic.radius.md.value,
        padding: tokens.core.spacing[2].value,
      }}
    >
      <div style={{ fontSize: '18px', fontWeight: 600 }}>{label} details</div>
      <TableContainer>
        <Table>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key} hover>
                <TableCell sx={{ fontWeight: 600 }}>{row.key}</TableCell>
                <TableCell>{renderValue(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};
